import React, {useRef, useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import toast from "react-hot-toast";
import QrScanner from "qr-scanner";
import API from "../api/config";
import WLogo from "../assets/QRscriptsLogo.png";
import GalleryViewer from "./GalleryViewer.jsx";
import imageCompression from "browser-image-compression";
import {FiCamera, FiUpload, FiLink, FiImage, FiEye, FiEyeOff, FiPhone, FiMail} from "react-icons/fi";
import {IoLogoWhatsapp} from "react-icons/io";

/* ----------------- helpers ----------------- */
const fileToBase64 = (file) =>
    new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
    });

/* ----------------- component ----------------- */
const RegisterOrRedirect = () => {
    const {qrId} = useParams();
    const videoRef = useRef(null);
    // const scannerRef = useRef(null);
    const [checking, setChecking] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [mode, setMode] = useState("url");
    const [url, setUrl] = useState("");
    const [images, setImages] = useState([]);
    const [ownerName, setOwnerName] = useState("");
    const [email, setEmail] = useState("");
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState(""); // Default empty
    const [callme, setCallme] = useState("");
    const [boxNote, setBoxNote] = useState("");
    const [acceptedTerms, setAcceptedTerms] = useState(true);
    const [accessType, setAccessType] = useState("public");
    const [qrPassword, setQrPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [enteredPassword, setEnteredPassword] = useState("");
    const [showEnteredPassword, setShowEnteredPassword] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeGenerator, setActiveGenerator] = useState(null);
    const [webUrl, setWebUrl] = useState("");
    const [waPhone, setWaPhone] = useState("");
    const [waMessage, setWaMessage] = useState("");

    // Email/Call States
    const [emailTo, setEmailTo] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");

    const openGenerator = (type) => {
        setActiveGenerator(type);
        setIsModalOpen(true);
    };

    const getCleanPhone = (input) => {
        let digits = input.replace(/[\s\-()+.]/g, "");
        const stripped = digits.replace(/^0+/, "");
        if (stripped.length < 7 || stripped.length > 15) return null;
        return stripped;
    };

    const applyWhatsAppLink = () => {
        const cleanNumber = getCleanPhone(waPhone);
        if (!cleanNumber) return toast.error("Please enter a valid phone number");
        const encodedMessage = waMessage ? encodeURIComponent(waMessage) : "";
        setUrl(`https://api.whatsapp.com/send/?phone=${cleanNumber}&text=${encodedMessage}`);
        setIsModalOpen(false);
        toast.success("WhatsApp link applied!");
    };

    const applyEmailLink = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailTo)) return toast.error("Please enter a valid email address");
        const encodedBody = encodeURIComponent(emailBody).replace(/%0A/g, "%0D%0A");
        setUrl(`mailto:${emailTo}?subject=${encodeURIComponent(emailSubject)}&body=${encodedBody}`);
        setIsModalOpen(false);
        toast.success("Email link applied!");
    };

    const applyCallLink = () => {
        const cleanNumber = phoneNumber.replace(/[\s\-()+.]/g, "");
        if (cleanNumber.length < 7) return toast.error("Phone number is too short");
        setUrl(`tel:+${cleanNumber}`);
        setIsModalOpen(false);
        toast.success("Call link applied!");
    };

    const applyWebLink = () => {
        let input = webUrl.trim();
        if (!input) return toast.error("Please enter a URL");
        if (!/^https?:\/\//i.test(input)) input = "https://" + input;
        setUrl(input);
        setIsModalOpen(false);
        toast.success("Web link applied!");
    };

    /* ----------------- STEP 1: AUTO CHECK ----------------- */
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const res = await API.get(`/registrations/scan/${qrId}`);
                if (res.data.action === "REDIRECT") {
                    window.location.replace(res.data.url);
                    return; // Don't turn off 'checking' to keep the loader visible during redirect
                }
                if (res.data.action === "SHOW_GALLERY") {
                    setView("gallery");
                } else if (res.data.action === "PASSWORD_REQUIRED") {
                    setView("password");
                } else {
                    setView("register");
                }
            } catch (err) {
                setView("register");
            } finally {
                setChecking(false);
            }
        };
        checkStatus();
    }, [qrId]);

    /* ----------------- STEP 2: QR SCAN (URL ONLY) ----------------- */
    useEffect(() => {
        if (!scanning || !videoRef.current) return;
        let scanner;
        const startScanner = async () => {
            try {
                scanner = new QrScanner(
                    videoRef.current,
                    (result) => {
                        if (!result?.data) return;
                        scanner.stop();
                        setScanning(false);
                        if (/^https?:\/\//i.test(result.data)) {
                            setMode("url");
                            setUrl(result.data);
                            toast.success("URL scanned successfully");
                        } else {
                            toast.error("Scanned QR does not contain a valid URL");
                        }
                    },
                    {highlightScanRegion: true, highlightCodeOutline: true}
                );
                await scanner.start();
            } catch (err) {
                toast.error("Camera access failed");
                setScanning(false);
            }
        };
        startScanner();
        return () => scanner?.stop();
    }, [scanning]);

    /* ----------------- IMAGE HANDLING ----------------- */
    const compressImage = async (file) => {
        const options = {maxSizeMB: 0.25, maxWidthOrHeight: 1280, useWebWorker: true};
        return await imageCompression(file, options);
    };

    const onFiles = async (fileList) => {
        const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
        if (images.length + files.length > 5) return toast.error("Maximum 5 images allowed");
        try {
            const compressedBase64s = [];
            for (const file of files) {
                const compressedFile = await compressImage(file);
                const base64 = await fileToBase64(compressedFile);
                compressedBase64s.push(base64);
            }
            setImages((prev) => [...prev, ...compressedBase64s]);
        } catch (err) {
            toast.error("Failed to process image");
        }
    };

    const removeImage = (index) => setImages((prev) => prev.filter((_, i) => i !== index));

    /* ----------------- SUBMIT ----------------- */
    const submit = async () => {
        if (!ownerName || !email) return toast.error("Name and Email are required");
        if (mode === "url" && !url) return toast.error("URL is required");
        if (mode === "images" && images.length === 0) return toast.error("Please add at least one image");
        if (accessType === "private" && !qrPassword) return toast.error("Password required for Private QR");

        try {
            setSaving(true);
            await API.post(`/registrations/register/${qrId}`, {
                owner_name: ownerName,
                callme,
                email,
                format: mode === "url" ? "Links" : "Photos",
                url: mode === "url" ? url : null,
                images: mode === "images" ? images : [],
                box_note: mode === "images" ? boxNote : null,
                access_type: accessType,
                password: accessType === "private" ? qrPassword : null,
            });
            toast.success("QR registered successfully Now Redirecting to the registered function.. ");
            setTimeout(() => window.location.reload(), 1500);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Registration failed");
        } finally {
            setSaving(false);
        }
    };

    /* --------------------------------------------------
       UI RENDERING
    -------------------------------------------------- */

    // 1. LOADING STATE

    if (checking) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-slate-700 to-slate-900 p-4 text-white">
                <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-white mb-6"></div>

                <div className="text-center">
                    <span className="text-xl font-semibold tracking-wide animate-pulse block">
                        Verifying QR Status...
                    </span>
                    <span className="text-sm text-slate-400 mt-2 block italic">Connecting you to your destination</span>
                </div>
            </div>
        );
    }

    // 2. GALLERY VIEW
    if (view === "gallery") {
        return <GalleryViewer qrId={qrId} onClose={() => setView("register")} />;
    }

    // 3. PASSWORD VIEW
    if (view === "password") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="bg-white p-6 rounded-2xl w-full max-w-sm">
                    <h2 className="text-lg font-semibold mb-4 text-center">This QR is Private</h2>
                    <div className="relative mb-4">
                        <input
                            type={showEnteredPassword ? "text" : "password"}
                            value={enteredPassword}
                            onChange={(e) => setEnteredPassword(e.target.value)}
                            placeholder="Enter password"
                            className="w-full p-3 border rounded-xl pr-10"
                        />
                        <button
                            type="button"
                            onClick={() => setShowEnteredPassword(!showEnteredPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                            {showEnteredPassword ? <FiEyeOff /> : <FiEye />}
                        </button>
                    </div>
                    <button
                        onClick={async () => {
                            try {
                                const res = await API.post(`/registrations/verify/${qrId}`, {
                                    password: enteredPassword,
                                });
                                if (res.data.action === "REDIRECT") window.location.replace(res.data.url);
                                if (res.data.action === "SHOW_GALLERY") setView("gallery");
                            } catch {
                                toast.error("Incorrect password");
                            }
                        }}
                        className="w-full bg-pink-600 text-white py-2 rounded-xl"
                    >
                        Unlock
                    </button>
                </div>
            </div>
        );
    }

    // 4. REGISTRATION VIEW (ONLY SHOWS IF CONFIRMED UNREGISTERED)
    if (view === "register") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-700 to-slate-900 p-4">
                <div className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl">
                    <div className="flex justify-center mb-2">
                        <img src={WLogo} alt="QR Scripts" className="h-20" />
                    </div>

                    <button onClick={() => setScanning(true)} className="w-full border rounded-2xl p-4 mb-4">
                        <FiCamera className="mx-auto text-3xl text-pink-500 mb-2" />
                        <div className="font-semibold text-center">Scan QR (Optional)</div>
                    </button>

                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setMode("url")}
                            className={`flex-1 py-2 rounded-xl border flex items-center justify-center gap-2 ${
                                mode === "url" ? "bg-black text-white" : ""
                            }`}
                        >
                            <FiLink /> Links
                        </button>
                        <button
                            onClick={() => setMode("images")}
                            className={`flex-1 py-2 rounded-xl border flex items-center justify-center gap-2 ${
                                mode === "images" ? "bg-black text-white" : ""
                            }`}
                        >
                            <FiImage /> Photos
                        </button>
                    </div>

                    {mode === "url" && (
                        <>
                            <div className="flex justify-around mb-4 bg-gray-50 p-3 rounded-2xl border border-dashed border-gray-200">
                                <div
                                    onClick={() => openGenerator("web")}
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <div className="p-2 bg-white rounded-full border shadow-sm">
                                        <FiLink className="text-black" />
                                    </div>
                                    <span className="text-[10px] font-bold mt-1">WEB</span>
                                </div>
                                <div
                                    onClick={() => openGenerator("whatsapp")}
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <div className="p-2 bg-white rounded-full border shadow-sm">
                                        <IoLogoWhatsapp className="text-green-500" />
                                    </div>
                                    <span className="text-[10px] font-bold mt-1">WHATSAPP</span>
                                </div>
                                <div
                                    onClick={() => openGenerator("email")}
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <div className="p-2 bg-white rounded-full border shadow-sm">
                                        <FiMail className="text-orange-500" />
                                    </div>
                                    <span className="text-[10px] font-bold mt-1">EMAIL</span>
                                </div>
                                <div
                                    onClick={() => openGenerator("call")}
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <div className="p-2 bg-white rounded-full border shadow-sm">
                                        <FiPhone className="text-purple-500" />
                                    </div>
                                    <span className="text-[10px] font-bold mt-1">CALL</span>
                                </div>
                            </div>
                            <div className="relative mb-4">
                                <input
                                    value={url}
                                    readOnly
                                    placeholder="Use the icons above to generate a link"
                                    className="w-full p-3 border rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed italic"
                                />
                                {url && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-green-100 text-green-600 px-2 py-1 rounded-md font-bold">
                                        Generated
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {mode === "images" && (
                        <>
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    onFiles(e.dataTransfer.files);
                                }}
                                className="border-2 border-dashed rounded-xl p-4 mb-4"
                            >
                                <FiUpload className="mx-auto text-2xl mb-2" />
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => onFiles(e.target.files)}
                                    className="hidden"
                                    id="img"
                                />
                                <label
                                    htmlFor="img"
                                    className="block text-center text-xs underline cursor-pointer mb-3"
                                >
                                    browse files
                                </label>
                                {images.length > 0 && (
                                    <div className="grid grid-cols-3 gap-2">
                                        {images.map((img, idx) => (
                                            <div key={idx} className="relative">
                                                <img src={img} className="w-full h-20 object-cover rounded-lg" />
                                                <button
                                                    onClick={() => removeImage(idx)}
                                                    className="absolute top-0 right-0 bg-black/70 text-white rounded-full w-5 h-5"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <input
                                value={boxNote}
                                onChange={(e) => setBoxNote(e.target.value)}
                                placeholder="Add a note"
                                className="w-full p-3 border rounded-xl mb-4"
                            />
                        </>
                    )}

                    <input
                        value={callme}
                        onChange={(e) => setCallme(e.target.value)}
                        placeholder="Suggest a name for this item"
                        className="w-full p-3 border rounded-xl mb-3"
                    />
                    <input
                        value={ownerName}
                        onChange={(e) => setOwnerName(e.target.value)}
                        placeholder="Your name"
                        className="w-full p-3 border rounded-xl mb-3"
                    />
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email"
                        className="w-full p-3 border rounded-xl mb-4"
                    />

                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setAccessType("public")}
                            className={`flex-1 py-2 rounded-xl border ${
                                accessType === "public" ? "bg-black text-white" : ""
                            }`}
                        >
                            Public
                        </button>
                        <button
                            onClick={() => setAccessType("private")}
                            className={`flex-1 py-2 rounded-xl border ${
                                accessType === "private" ? "bg-black text-white" : ""
                            }`}
                        >
                            Private
                        </button>
                    </div>

                    {accessType === "private" && (
                        <div className="relative mb-4">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={qrPassword}
                                onChange={(e) => setQrPassword(e.target.value)}
                                placeholder="Set QR password"
                                className="w-full p-3 border rounded-xl"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                            >
                                {showPassword ? <FiEyeOff /> : <FiEye />}
                            </button>
                        </div>
                    )}

                    <button
                        onClick={submit}
                        disabled={saving || !acceptedTerms}
                        className={`w-full py-3 rounded-xl font-semibold text-white ${
                            saving || !acceptedTerms
                                ? "bg-pink-600/60 cursor-not-allowed"
                                : "bg-pink-600 hover:bg-pink-700"
                        }`}
                    >
                        {saving ? "Saving..." : "Save & Activate QR"}
                    </button>

                    <div className="flex items-start gap-2 text-[11px] text-gray-500 mt-4">
                        <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            className="mt-1"
                        />
                        <label>
                            Agree to{" "}
                            <a href="#" className="text-pink-600 underline">
                                Privacy Policy
                            </a>{" "}
                            &{" "}
                            <a href="#" className="text-pink-600 underline">
                                Terms
                            </a>
                        </label>
                    </div>
                </div>

                {/* --- MODALS --- */}
                {scanning && (
                    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
                        <div className="bg-white p-4 rounded-xl w-full max-w-sm">
                            <video ref={videoRef} className="rounded-xl w-full" />
                            <button
                                onClick={() => setScanning(false)}
                                className="w-full mt-4 py-2 bg-gray-600 text-white rounded-xl"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <div className="bg-white w-full max-w-md rounded-3xl p-6 relative">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute top-4 right-4 text-gray-400"
                            >
                                ✕
                            </button>
                            <h2 className="text-xl font-bold mb-6 capitalize text-center">
                                {activeGenerator} Generator
                            </h2>
                            <div className="space-y-4">
                                {activeGenerator === "web" && (
                                    <input
                                        type="text"
                                        placeholder="example.com"
                                        value={webUrl}
                                        onChange={(e) => setWebUrl(e.target.value)}
                                        className="w-full p-3 border rounded-xl"
                                    />
                                )}
                                {activeGenerator === "email" && (
                                    <>
                                        <input
                                            type="email"
                                            placeholder="Recipient Email"
                                            value={emailTo}
                                            onChange={(e) => setEmailTo(e.target.value)}
                                            className="w-full p-3 border rounded-xl"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Subject"
                                            value={emailSubject}
                                            onChange={(e) => setEmailSubject(e.target.value)}
                                            className="w-full p-3 border rounded-xl"
                                        />
                                        <textarea
                                            placeholder="Message Body"
                                            value={emailBody}
                                            onChange={(e) => setEmailBody(e.target.value)}
                                            className="w-full p-3 border rounded-xl min-h-[100px]"
                                        />
                                    </>
                                )}
                                {activeGenerator === "call" && (
                                    <input
                                        type="text"
                                        placeholder="+91 9876543210"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        className="w-full p-3 border rounded-xl"
                                    />
                                )}
                                {activeGenerator === "whatsapp" && (
                                    <>
                                        <input
                                            type="text"
                                            placeholder="Phone Number"
                                            value={waPhone}
                                            onChange={(e) => setWaPhone(e.target.value)}
                                            className="w-full p-3 border rounded-xl"
                                        />
                                        <textarea
                                            placeholder="Message"
                                            value={waMessage}
                                            onChange={(e) => setWaMessage(e.target.value)}
                                            className="w-full p-3 border rounded-xl min-h-[100px]"
                                        />
                                    </>
                                )}
                            </div>
                            <div className="flex gap-3 mt-8">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 py-3 bg-gray-100 rounded-xl font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (activeGenerator === "web") applyWebLink();
                                        else if (activeGenerator === "email") applyEmailLink();
                                        else if (activeGenerator === "call") applyCallLink();
                                        else if (activeGenerator === "whatsapp") applyWhatsAppLink();
                                    }}
                                    className="flex-1 py-3 bg-pink-600 text-white rounded-xl font-semibold"
                                >
                                    Apply Link
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default RegisterOrRedirect;
